import { observer } from 'mobx-react'
import React, { lazy, Suspense, useContext, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Controller, useForm } from 'react-hook-form'

import { PasswordInput } from '@/components/forms/PasswordInput'
import { RadioInput } from '@/components/forms/RadioInput'
import { TextInput } from '@/components/forms/TextInput'
import { Button } from '@/components/ui/Button'
import { LegalLinks } from '@/components/ui/LegalLinks'
import { StateContainer } from '@/components/ui/StateContainer'
import {
  confirmEmailChange,
  confirmPhoneChange,
  requestEmailChange,
  requestPhoneChange,
  updateUser,
  updateUserPassword,
} from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast, verifyPassword } from '@/utils/helpers'

// Lazy-loaded so libphonenumber-js (~440 KB) stays out of the main bundle
const PhoneNumberInput = lazy(() => import('@/components/forms/PhoneNumberInput/PhoneNumberInput'))

/**
 *
 * UserProfile
 *
 */
const UserProfile = observer(() => {
  // Context
  const {
    error: loadingError,
    loading,
    getUpdatedUser,
    setCurrentUser,
    user,
    isDealerUser,
    isImpersonating,
    isWpcUser,
  } = useContext(UserStoreContext)

  // State
  const [loadingProfile, setLoadingProfile] = useState(false)
  const [loadingPassword, setLoadingPassword] = useState(false)
  const [loadingEmail, setLoadingEmail] = useState(false)
  const [loadingPhone, setLoadingPhone] = useState(false)
  const [loadingMfa, setLoadingMfa] = useState(false)
  const [verifyMfa, setVerifyMfa] = useState(null)

  useEffect(() => {
    getUpdatedUser(user.id)
  }, [])

  const {
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors, isDirty: profileIsDirty },
    register: profileRegister,
    reset: profileReset,
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
    },
  })

  const {
    control: passwordControl,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors, isDirty: passwordIsDirty },
    reset: resetPasswordForm,
    watch,
  } = useForm({
    defaultValues: {
      oldPassword: null,
      newPassword: null,
      confirmPassword: null,
    },
  })

  const {
    handleSubmit: handleEmailSubmit,
    formState: { errors: emailErrors, isDirty: emailIsDirty },
    getValues: getEmailValues,
    register: emailRegister,
    reset: emailReset,
  } = useForm({
    defaultValues: {
      email: '',
      code: '',
    },
  })

  const {
    control: phoneControl,
    handleSubmit: handlePhoneSubmit,
    formState: { errors: phoneErrors, isDirty: phoneIsDirty },
    getValues: getPhoneValues,
    register: phoneRegister,
    reset: phoneReset,
  } = useForm({
    defaultValues: {
      phoneNumber: '',
      code: '',
    },
  })

  const {
    control: multiFactorControl,
    handleSubmit: handleMultiFactorSubmit,
    formState: { isDirty: multiFactorIsDirty },
    reset: mfaReset,
  } = useForm({
    defaultValues: {
      mfaDevice: '',
    },
  })

  useEffect(() => {
    if (user) {
      profileReset({
        firstName: user.firstName,
        lastName: user.lastName,
        displayName: user.displayName,
        email: user.email,
      })

      emailReset({
        email: user.email,
      })

      phoneReset({
        phoneNumber: user.phoneNumber,
      })

      mfaReset({
        mfaDevice: user.mfaDevice,
      })
    }
  }, [user])

  const handleSuccesses = (message) => toast(message, 'success')
  const handleErrors = (message) => toast(message, 'error')

  /**
   * Handles submitting changes to the user's profile.
   * - If the user is changing either their email or phone number, these get handled
   *  separately. Both of these changes require a verification code.
   * @param {object} data
   */
  const onProfileSubmit = async (data) =>
    updateUser(
      {
        id: user.id,
        ...data,
      },
      handleErrors,
      setLoadingProfile,
      (m) => handleSuccesses(m),
      setCurrentUser,
    )

  /**
   * Handles requesting a change to the user's email address, and sending the verification
   * code.
   * @param {object} data
   */
  const onEmailSubmit = async (data) => {
    if (verifyMfa === 'email') {
      await confirmEmailChange(
        data.code,
        handleErrors,
        setLoadingEmail,
        (m) => {
          handleSuccesses(m)
          setVerifyMfa(null)
        },
        setCurrentUser,
      )
    } else {
      await requestEmailChange(data.email, handleErrors, setLoadingEmail, (m) => {
        handleSuccesses(m)
        setVerifyMfa('email')
      })
    }
  }

  /**
   * Handles requesting a change to the user's phone number, and sending the verification
   * code.
   * @param {object} data
   */
  const onPhoneSubmit = async (data) => {
    if (verifyMfa === 'phone') {
      await confirmPhoneChange(
        data.code,
        handleErrors,
        setLoadingPhone,
        (m) => {
          handleSuccesses(m)
          setVerifyMfa(null)
        },
        setCurrentUser,
      )
    } else {
      await requestPhoneChange(data.phoneNumber, handleErrors, setLoadingPhone, (m) => {
        if (data.phoneNumber !== null && data.phoneNumber.length > 0) {
          handleSuccesses(m)
          setVerifyMfa('phone')
        } else {
          handleSuccesses('Phone number removed.')
        }
      })
    }
  }

  return (
    <>
      <Helmet>
        <title>Profile</title>
      </Helmet>

      <StateContainer error={loadingError} loading={loading}>
        <div className="flex flex-col space-y-8 place-self-center px-10 md:w-[75%] md:min-w-[650px] md:max-w-5xl md:px-10">
          <div className="bg-brownGray-100 grid grid-cols-1 gap-x-8 gap-y-4 rounded-lg px-4 py-8 shadow-md sm:grid-cols-5 sm:gap-y-0">
            <div className="col-span-full sm:col-span-2">
              <h2 className="text-primary text-base font-semibold leading-7">
                Personal Information
              </h2>
              {isDealerUser && (
                <p className="text-primary mt-1 text-sm leading-6">
                  Update any profile information.
                </p>
              )}
            </div>

            <form className="col-span-full sm:col-span-3">
              <div className="grid grid-cols-2 gap-4">
                {/* <div className="col-span-full">{renderUserAvatar()}</div> */}

                <div className="col-span-1">
                  <TextInput
                    data-testid="firstName"
                    error={profileErrors.firstName && 'This field is required'}
                    fullWidth
                    id="firstName"
                    name="firstName"
                    label="First Name"
                    placeholder="First Name"
                    disabled={isWpcUser}
                    {...profileRegister('firstName', { required: true })}
                  />
                </div>

                <div className="col-span-1">
                  <TextInput
                    data-testid="lastName"
                    error={profileErrors.lastName && 'This field is required'}
                    fullWidth
                    id="lastName"
                    name="lastName"
                    label="Last Name"
                    placeholder="Last Name"
                    disabled={isWpcUser}
                    {...profileRegister('lastName', { required: true })}
                  />
                </div>
              </div>

              {!isDealerUser && (
                <div className="col-span-1 mt-4">
                  <TextInput
                    data-testid="displayName"
                    error={profileErrors.displayName && 'This field is required'}
                    fullWidth
                    id="displayName"
                    name="displayName"
                    label="Display Name"
                    placeholder="Display Name"
                    disabled
                    {...profileRegister('displayName', { required: true })}
                  />
                </div>
              )}

              {isDealerUser && (
                <div className="mt-4 flex flex-col place-items-end space-y-2">
                  <Button
                    disabled={loadingProfile || !profileIsDirty || isImpersonating}
                    onClick={handleProfileSubmit(onProfileSubmit)}
                    label="Save Changes"
                    loading={loadingProfile}
                    type="button"
                    size="sm"
                  />
                </div>
              )}
            </form>
          </div>

          {isDealerUser && (
            <div className="bg-brownGray-100 grid grid-cols-1 gap-x-8 gap-y-4 rounded-lg px-4 py-8 shadow-md sm:grid-cols-5 sm:gap-y-0">
              <div className="col-span-full sm:col-span-2">
                <h2 className="text-primary text-base font-semibold leading-7">Change password</h2>
                <p className="text-primary mt-1 text-sm leading-6">
                  Update the password associated with your account.
                </p>
              </div>

              <form className="col-span-full sm:col-span-3">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="col-span-full">
                    <Controller
                      name="oldPassword"
                      control={passwordControl}
                      render={({ field: { onChange, value } }) => (
                        <PasswordInput
                          autoComplete="password"
                          data-testid="oldPassword"
                          error={passwordErrors.oldPassword && 'This field is required'}
                          fullWidth
                          id="oldPassword"
                          label="Current Password"
                          placeholder="Current Password"
                          onChange={onChange}
                          value={value}
                          showTooltip={false}
                        />
                      )}
                      rules={{ required: true }}
                    />
                  </div>

                  <div className="col-span-full">
                    <Controller
                      name="newPassword"
                      control={passwordControl}
                      render={({ field: { onChange, value } }) => (
                        <PasswordInput
                          autoComplete="password"
                          data-testid="newPassword"
                          error={
                            passwordErrors.newPassword &&
                            (passwordErrors.newPassword.message || 'This field is required')
                          }
                          fullWidth
                          id="newPassword"
                          name="newPassword"
                          label="New Password"
                          placeholder="New Password"
                          onChange={onChange}
                          value={value}
                        />
                      )}
                      rules={{ required: true, validate: (value) => verifyPassword(value) }}
                    />
                  </div>

                  <div className="col-span-full">
                    <Controller
                      name="confirmPassword"
                      control={passwordControl}
                      render={({ field: { onChange, value } }) => (
                        <PasswordInput
                          autoComplete="password"
                          data-testid="confirmPassword"
                          error={
                            passwordErrors.confirmPassword &&
                            (passwordErrors.confirmPassword.message || 'This field is required')
                          }
                          fullWidth
                          id="confirmPassword"
                          name="confirmPassword"
                          label="Confirm Password"
                          placeholder="Confirm Password"
                          onChange={onChange}
                          value={value}
                        />
                      )}
                      rules={{
                        required: true,
                        validate: (value) => {
                          const err = verifyPassword(value)
                          if (err) return err
                          if (watch('newPassword') !== value) {
                            return 'Passwords do not match'
                          }
                          return undefined
                        },
                      }}
                    />
                  </div>
                </div>

                <div className="mt-4 flex flex-col place-items-end space-y-2">
                  <Button
                    disabled={loadingPassword || !passwordIsDirty || isImpersonating}
                    onClick={handlePasswordSubmit((data) =>
                      updateUserPassword(
                        {
                          id: user.id,
                          ...data,
                        },
                        handleErrors,
                        setLoadingPassword,
                        (m) => {
                          handleSuccesses(m)
                          resetPasswordForm()
                        },
                      ),
                    )}
                    label="Save Changes"
                    loading={loadingPassword}
                    type="button"
                    size="sm"
                  />
                </div>
              </form>
            </div>
          )}

          <div className="bg-brownGray-100 grid grid-cols-1 gap-x-8 gap-y-4 rounded-lg px-4 py-8 shadow-md sm:grid-cols-5 sm:gap-y-0">
            <div className="col-span-full sm:col-span-2">
              <h2 className="text-primary text-base font-semibold leading-7">Email Address</h2>
              {isDealerUser && (
                <p className="text-primary mt-1 text-sm leading-6">
                  Update the email address associated with your account.
                </p>
              )}
            </div>

            <form className="col-span-full sm:col-span-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-full">
                  <TextInput
                    data-testid="email"
                    error={emailErrors.email && 'This field is required'}
                    fullWidth
                    id="email"
                    name="email"
                    label="Email"
                    placeholder="Email"
                    disabled={isWpcUser}
                    {...emailRegister('email', { required: true })}
                  />
                </div>

                {verifyMfa === 'email' && (
                  <div className="col-span-full flex flex-col">
                    <TextInput
                      data-testid="code"
                      error={profileErrors.code && 'This field is required'}
                      fullWidth
                      id="code"
                      name="code"
                      label="Verification Code"
                      placeholder="Verification Code"
                      {...emailRegister('code', { required: true })}
                    />

                    <button
                      className="mr-1 place-self-end"
                      type="button"
                      onClick={async () => {
                        await requestEmailChange(
                          getEmailValues().email,
                          handleErrors,
                          setLoadingEmail,
                          (m) => {
                            handleSuccesses(m)
                            setVerifyMfa('email')
                          },
                        )
                      }}
                    >
                      <span className="text-sm font-medium text-blue-500 hover:text-blue-600">
                        Resend Verification Code
                      </span>
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-4 flex flex-row justify-end space-x-2">
                {verifyMfa === 'email' && (
                  <Button
                    background="bg-white"
                    loading={loading}
                    onClick={() => {
                      setVerifyMfa(null)
                      emailReset()
                    }}
                    outlined
                    label="Cancel"
                    size="sm"
                  />
                )}
                {isDealerUser && (
                  <Button
                    disabled={loadingEmail || !emailIsDirty || isImpersonating}
                    onClick={handleEmailSubmit(onEmailSubmit)}
                    label={verifyMfa === 'email' ? 'Confirm Change' : 'Save Changes'}
                    loading={loadingEmail}
                    type="button"
                    size="sm"
                  />
                )}
              </div>
            </form>
          </div>

          {isDealerUser && (
            <>
              <div className="bg-brownGray-100 grid grid-cols-1 gap-x-8 gap-y-4 rounded-lg px-4 py-8 shadow-md sm:grid-cols-5 sm:gap-y-0">
                <div className="col-span-full sm:col-span-2">
                  <h2 className="text-primary text-base font-semibold leading-7">Phone Number</h2>
                  <p className="text-primary mt-1 text-sm leading-6">
                    Update the phone number associated with your account. By providing your phone
                    number, you authorize WARP to send text messages for authentication purposes.
                    Message and data rates apply.
                  </p>
                </div>

                <form className="col-span-full sm:col-span-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-full">
                      <Suspense fallback={null}>
                        <PhoneNumberInput
                          control={phoneControl}
                          disabled={loadingPhone}
                          error={phoneErrors.phoneNumber}
                          name="phoneNumber"
                        />
                      </Suspense>
                    </div>

                    {verifyMfa === 'phone' && (
                      <div className="col-span-full flex flex-col">
                        <TextInput
                          data-testid="code"
                          disabled={loadingPhone}
                          error={phoneErrors.code && 'This field is required'}
                          fullWidth
                          id="code"
                          name="code"
                          label="Verification Code"
                          placeholder="XXXXXXX"
                          {...phoneRegister('code', { required: true })}
                        />

                        <button
                          className="mr-1 place-self-end"
                          disabled={loadingPhone}
                          type="button"
                          onClick={async () => {
                            const { phoneNumber } = getPhoneValues()
                            await requestPhoneChange(
                              phoneNumber,
                              handleErrors,
                              setLoadingPhone,
                              (m) => {
                                if (phoneNumber !== null && phoneNumber.length > 0) {
                                  handleSuccesses(m)
                                  setVerifyMfa('phone')
                                } else {
                                  handleSuccesses('Phone number removed.')
                                }
                              },
                            )
                          }}
                        >
                          <span className="text-purple text-sm font-medium">
                            Resend Verification Code
                          </span>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex flex-row justify-end space-x-2">
                    {verifyMfa === 'phone' && (
                      <Button
                        background="bg-white"
                        loading={loading}
                        onClick={() => {
                          setVerifyMfa(null)
                          phoneReset()
                        }}
                        outlined
                        label="Cancel"
                        size="sm"
                      />
                    )}

                    <Button
                      disabled={loadingPhone || !phoneIsDirty || isImpersonating}
                      onClick={handlePhoneSubmit(onPhoneSubmit)}
                      label={verifyMfa === 'phone' ? 'Confirm Change' : 'Save Changes'}
                      loading={loadingPhone}
                      type="button"
                      size="sm"
                    />
                  </div>
                </form>
              </div>

              <div className="bg-brownGray-100 grid grid-cols-1 gap-x-8 gap-y-4 rounded-lg px-4 py-8 shadow-md sm:grid-cols-5 sm:gap-y-0">
                <div className="col-span-full sm:col-span-2">
                  <h2 className="text-primary text-base font-semibold leading-7">
                    Multi-Factor Authentication
                  </h2>
                  <p className="text-primary mt-1 text-sm leading-6">
                    Configure where you would like to receive your authentication codes.
                  </p>
                </div>

                <form className="col-span-full sm:col-span-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-full">
                      <Controller
                        name="mfaDevice"
                        control={multiFactorControl}
                        render={({ field: { onChange, value, ref } }) => (
                          <RadioInput
                            className="mt-4 self-center"
                            horizontal
                            name="mfaDevice"
                            options={[
                              {
                                id: 'Phone',
                                label: 'Phone Number',
                                disabled: user.phoneNumber === null || user.phoneNumber === '',
                              },
                              { id: 'Email', label: 'Email Address' },
                            ]}
                            onChange={onChange}
                            value={value}
                            ref={ref}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col place-items-end space-y-2">
                    <Button
                      disabled={loadingMfa || !multiFactorIsDirty || isImpersonating}
                      onClick={handleMultiFactorSubmit((data) =>
                        updateUser(
                          {
                            id: user.id,
                            ...data,
                          },
                          handleErrors,
                          setLoadingMfa,
                          (m) => handleSuccesses(m),
                          setCurrentUser,
                        ),
                      )}
                      label="Save Changes"
                      loading={loadingMfa}
                      type="button"
                      size="sm"
                    />
                  </div>
                </form>
              </div>
            </>
          )}

          <LegalLinks showTerms />

          <div className="h-[10px] flex-none" aria-hidden="true" />
        </div>
      </StateContainer>
    </>
  )
})

export default UserProfile
